import { useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  InputAdornment,
  Button,
  Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { usePartyManager } from './PartyManagerContext';
import { EDIT_SOURCE, CODEMETA_SOURCE } from './partyManagerUtils';

const ArrayField = ({ fieldKey, label }) => {
  const { partyData, setPartyData, isPartyManagerLoading } = usePartyManager();
  const [newItemValue, setNewItemValue] = useState('');

  const items = partyData[fieldKey] || [];

  const getButtonIcon = (item) => {
    if (item.selected === 'edit') return EDIT_SOURCE.icon;
    if (item.selected === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    return '?';
  };

  const updateItems = (newItems) => {
    setPartyData((prev) => ({
      ...prev,
      [fieldKey]: newItems,
    }));
  };

  const handleAddItem = () => {
    const trimmedValue = newItemValue.trim();
    if (trimmedValue) {
      const newItem = {
        codemeta: null,
        edit: trimmedValue,
        selected: 'edit',
      };
      updateItems([...items, newItem]);
      setNewItemValue('');
    }
  };

  const handleRemoveItem = (indexToRemove) => {
    const newItems = items.filter((_, index) => index !== indexToRemove);
    updateItems(newItems);
  };

  const handleUpdateItem = (indexToUpdate, value) => {
    const newItems = items?.map((item, index) => {
      if (index === indexToUpdate) {
        return { ...item, edit: value, selected: 'edit' };
      }
      return item;
    });
    updateItems(newItems);
  };

  const handleCycleSource = (indexToCycle) => {
    const newItems = items?.map((item, index) => {
      if (index === indexToCycle && item.codemeta) {
        const nextSelected = item.selected === 'codemeta' ? 'edit' : 'codemeta';
        return { ...item, selected: nextSelected };
      }
      return item;
    });
    updateItems(newItems);
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
        {label}
      </Typography>
      {isPartyManagerLoading ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items?.map((item, index) => {
              const displayValue =
                item.selected === 'edit' ? item.edit : item[item.selected];
              const hasCodemetaSource = !!item.codemeta;

              return (
                <Box
                  key={index}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <TextField
                    value={displayValue || ''}
                    onChange={(e) => handleUpdateItem(index, e.target.value)}
                    size="small"
                    variant="outlined"
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87)',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.38)',
                        '&.MuiInputLabel-shrink': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      },
                    }}
                    placeholder="Wert"
                    autoComplete="nope"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" sx={{ mr: -1.5 }}>
                          <IconButton
                            onClick={() => handleRemoveItem(index)}
                            aria-label="Eintrag löschen"
                            size="small"
                            sx={{
                              color: 'error.dark',
                              borderRadius: 1,
                              mr: 1,
                              '&:hover': {
                                backgroundColor: 'error.main',
                                color: 'common.white',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {hasCodemetaSource && (
                    <Button
                      title={`Quelle wechseln: Aktuell ${item.selected}`}
                      sx={{
                        width: '40px',
                        height: '40px',
                        minWidth: '40px',
                        maxWidth: '40px',
                      }}
                      onClick={() => handleCycleSource(index)}
                      variant="outlined"
                    >
                      {getButtonIcon(item)}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={{ mt: 1.5 }}>
            <TextField
              label={`Neue ${label.slice(0, -(label === 'Abteilungen' ? 2 : 1))} hinzufügen`}
              size="small"
              variant="outlined"
              fullWidth
              value={newItemValue}
              onChange={(e) => setNewItemValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddItem();
                }
              }}
              onBlur={handleAddItem}
              sx={{
                '& .MuiInputBase-input': {
                  color: 'rgba(0, 0, 0, 0.87)',
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(0, 0, 0, 0.38)',
                  '&.MuiInputLabel-shrink': {
                    color: 'rgba(0, 0, 0, 0.87)',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: -1.5 }}>
                    <IconButton
                      onClick={handleAddItem}
                      aria-label="Eintrag hinzufügen"
                      size="small"
                      disabled={!newItemValue.trim()}
                      tabIndex={-1}
                      sx={{
                        color: 'success.dark',
                        borderRadius: 1,
                        mr: 1,
                        '&:hover': {
                          backgroundColor: 'success.main',
                          color: 'common.white',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: 'action.disabledBackground',
                        },
                      }}
                    >
                      <AddIcon fontSize="inherit" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ArrayField;
